import { liveOwnedUploadNotices } from '~/features/vault/documents/documents.service.machine';
import { refreshShownVaultSection } from '~/features/vault/forceVaultPageRefresh';
import {
  ALERT_VIEW_STATE,
  DOCUMENTS_SERVICE,
  DOCUMENTS_STATE,
  EMPLOYER_ASSOCIATION_STATE,
  MALPRACTICE_STATE,
  NOTIFICATION_STATE,
  PROVIDER_ASSOCIATION_STATE,
} from '~/features/xstateSystem';
import { OnConnect, OnConnected, OnError, OnMessageReceived } from './events';
import {
  DOCUMENT_UPLOAD_COMPLETE_TITLE,
  isAiJobUpdateMessage,
  isChatbotMessage,
  isEphemeralNotification,
  isInsuranceParseCompleted,
  isNotificationMessage,
  isProviderAssociationMessage,
  parseWSMessage,
  type WSMessageCallback,
  type WSMessageEnvelope,
} from './message-types';
import { Scope } from './types';

/**
 * [MCV-1800] Whether the Documents page's upload batch has already told the user about the
 * upload this "Document Upload Complete" notification is for, so a second toast would only
 * repeat it. The notification names its document (`extraData.documentId`), and the batch records
 * the id of every file it stored without parsing (the only kind that gets this notification) in
 * the documents service:
 *  - an id the batch owns: drop it and take the mark away;
 *  - an id of a document the service already lists: it is not the file being uploaded, so keep it;
 *  - any other id: it may be the file being uploaded right now, whose POST response (and so its
 *    mark) the notification has beaten back, so it is dropped only while the service is
 *    uploading for the Documents view. Outside that window an unknown id is someone else's
 *    upload (registration CV, malpractice certificate, a delegate's) and keeps its toast, as
 *    that is its only completion feedback. A mark left behind for such an id is inert: ids are
 *    exact and expire.
 *  - no id (an API that does not send one): fall back to counting, i.e. drop it if the batch has
 *    any mark left (taking the oldest away), or while the service is uploading for the view.
 */
const claimOwnedUploadComplete = (
  system: Scope['self']['system'] | undefined,
  documentId: unknown
): boolean => {
  try {
    const service = system?.get(DOCUMENTS_SERVICE);
    if (!service) return false;
    const snapshot = service.getSnapshot();
    const { ownedUploadNotices, documentReferences, viewRef } = snapshot.context;
    const owned = liveOwnedUploadNotices(ownedUploadNotices);
    const uploadingForView = snapshot.matches('$_UPLOADING') && viewRef?.id === DOCUMENTS_STATE;

    if (typeof documentId === 'number') {
      if (owned.some(({ id }) => id === documentId)) {
        service.send({ type: 'ON_UPLOAD_NOTICE_CLAIMED', documentId });
        return true;
      }
      if (documentReferences.some(({ id }: { id: number }) => id === documentId)) return false;
      return uploadingForView;
    }

    if (owned.length > 0) {
      service.send({ type: 'ON_UPLOAD_NOTICE_CLAIMED' });
      return true;
    }
    return uploadingForView;
  } catch {
    return false;
  }
};

/**
 * Execute a generic callback configuration
 * This provides a unified way to handle any websocket message callback
 */
const executeCallback = async (scope: Scope, callback: WSMessageCallback): Promise<void> => {
  const { self } = scope;
  const system = self.system;

  // 1. Route to XState machine
  if (callback.targetMachine && system) {
    try {
      const machine = system.get(callback.targetMachine);
      if (machine && callback.eventType) {
        machine.send({
          type: callback.eventType,
          payload: callback.payload,
        });
      }
    } catch (error) {
      console.warn(`[WebSocket] Could not route to ${callback.targetMachine}:`, error);
    }
  }

  // 2. Show alert notification
  if (callback.showAlert && callback.alertConfig && system) {
    try {
      const alertView = system.get(ALERT_VIEW_STATE);
      if (alertView) {
        alertView.send({
          type: 'ON_SHOW_ALERT',
          payload: callback.alertConfig,
        });
      }
    } catch (error) {
      console.warn('[WebSocket] Could not show alert:', error);
    }
  }

  // 3. Navigate to URL
  if (callback.navigateTo) {
    window.location.href = callback.navigateTo;
  }

  // 4. Trigger service refetch
  if (callback.refetchService && callback.refetchEvent && system) {
    try {
      const service = system.get(callback.refetchService);
      if (service) {
        service.send({
          type: callback.refetchEvent,
        });
      }
    } catch (error) {
      console.warn(`[WebSocket] Could not trigger refetch on ${callback.refetchService}:`, error);
    }
  }

  // 5. Dispatch custom event
  if (callback.customEventName) {
    document.dispatchEvent(
      new CustomEvent(callback.customEventName, {
        detail: callback.payload,
      })
    );
  }
};

export const setUserId = (scope: Scope) => {
  const event = scope.event as OnConnect;
  return {
    userId: event.payload?.userId,
  };
};

export const setConnected = (scope: Scope) => {
  const event = scope.event as OnConnected;
  return {
    socket: event.payload.socket,
  };
};

export const setError = (scope: Scope) => {
  const event = scope.event as OnError;
  return {
    error: event.payload.error,
  };
};

/**
 * Route incoming WebSocket messages to appropriate handlers
 * This action:
 * 1. Parses the incoming message
 * 2. Determines the message type (chatbot, notification, etc.)
 * 3. Routes ephemeral notifications to alertView (via system.get)
 * 4. Routes persisted notifications to notificationState and a compatibility CustomEvent
 * 5. Dispatches chatbot messages via CustomEvent
 */
export const routeMessage = (scope: Scope) => {
  const { context, self } = scope;
  const event = scope.event as OnMessageReceived;

  const rawMessage = event.payload as unknown;
  const message =
    typeof rawMessage === 'string'
      ? parseWSMessage(rawMessage)
      : (rawMessage as WSMessageEnvelope | null);

  if (!message) {
    return;
  }

  if (!context.subscriptions.has(message.type)) {
    return;
  }

  if (isProviderAssociationMessage(message)) {
    console.debug('[WebSocket] provider-association message received:', message.data.eventType);
    if (message.data.employerId && message.data.providerId) {
      try {
        // NOTE:
        // - the providerAssociationState is controlled by EmployerUsers from the marketState
        // - the employerAssociationState is controlled by the Provider from the vaultState
        const providerAssociationState = self.system?.get(PROVIDER_ASSOCIATION_STATE);
        const employerAssociationState = self.system?.get(EMPLOYER_ASSOCIATION_STATE);
        const notificationState = self.system?.get(NOTIFICATION_STATE);

        if (providerAssociationState) {
          console.debug('[WebSocket] forwarding to providerAssociationState');
          providerAssociationState.send({ type: 'ON_WEBSOCKET_MESSAGE' });
        }
        if (employerAssociationState) {
          console.debug('[WebSocket] forwarding to employerAssociationState');
          employerAssociationState.send({ type: 'ON_WEBSOCKET_MESSAGE' });
        }
        if (notificationState) {
          console.debug('[WebSocket] forwarding to notificationState');
          notificationState.send({ type: 'ON_LOAD' });
        }
      } catch (error) {
        console.warn('[WebSocket] Could not trigger both machines:', error);
      }
    }

    // Also dispatch custom event for backward compatibility
    const eventName = `${context.config.eventNamespace}-provider-association`;
    document.dispatchEvent(
      new CustomEvent(eventName, {
        detail: message.data,
      })
    );
  }

  // Route based on message type
  if (isChatbotMessage(message)) {
    // Dispatch chatbot message via CustomEvent
    const eventName = `${context.config.eventNamespace}-chatbot`;
    document.dispatchEvent(
      new CustomEvent(eventName, {
        detail: message.data,
      })
    );
  }

  if (isNotificationMessage(message)) {
    const { notification, callback } = message.data;

    // Execute callback if present
    if (callback) {
      executeCallback(scope, callback);
    }

    // Ephemeral notifications go directly to alertView
    if (
      isEphemeralNotification(notification.persistence) &&
      notification.title === DOCUMENT_UPLOAD_COMPLETE_TITLE &&
      claimOwnedUploadComplete(self.system, notification.extraData?.documentId)
    ) {
      // [MCV-1800] The Documents page already summarised this upload. No refresh hangs on it:
      // documentsService takes notification.user as a no-op.
      console.debug('[WebSocket] Upload-complete notification handled by the upload summary');
    } else if (isEphemeralNotification(notification.persistence)) {
      try {
        const alertView = self.system?.get(ALERT_VIEW_STATE);
        if (alertView) {
          alertView.send({
            type: 'ON_SHOW_ALERT',
            payload: {
              text: notification.title,
              subText: notification.body,
              type:
                notification.severity === 'failure'
                  ? 'error'
                  : notification.severity === 'warning'
                    ? 'warning'
                    : notification.severity === 'success'
                      ? 'success'
                      : 'info',
              timeout: 5000,
            },
          });
          console.debug(
            '[WebSocket] Ephemeral notification routed to alertView:',
            notification.title
          );
        } else {
          console.warn('[WebSocket] alertView not available, falling back to CustomEvent');
          // Fallback to CustomEvent for backward compatibility
          const eventName = `${context.config.eventNamespace}-notification`;
          document.dispatchEvent(
            new CustomEvent(eventName, {
              detail: message.data,
            })
          );
        }
      } catch (error) {
        console.warn('[WebSocket] Could not route to AlertService:', error);
      }
    } else {
      // Persisted notifications refetch the durable server state so the bell
      // updates immediately. The WebSocket payload is only a delivery hint;
      // the notifications API remains the source of truth.
      try {
        const notificationState = self.system?.get(NOTIFICATION_STATE);
        if (notificationState) {
          notificationState.send({ type: 'ON_LOAD' });
        }
      } catch (error) {
        console.warn('[WebSocket] Could not refresh notificationState:', error);
      }

      // Keep the custom event for consumers outside the XState system.
      const eventName = `${context.config.eventNamespace}-notification`;
      document.dispatchEvent(
        new CustomEvent(eventName, {
          detail: message.data,
        })
      );
    }

    // Route notification.user messages to documentsService if available
    if (message.type === 'notification.user') {
      try {
        const documentsService = self.system?.get(DOCUMENTS_SERVICE);
        if (documentsService) {
          documentsService.send({
            type: 'ON_NOTIFICATION',
            payload: message.data,
          });
        }
      } catch (error) {
        console.warn('[WebSocket] Could not route user notification to documentsService:', error);
      }

      // [MCV-1753] A change made to the vault while the user is looking at that section (a
      // delegate's edit) refreshes it in place; the bell refetch above only updates the list.
      try {
        const sectionUrl = notification.extraData?.section?.url;
        if (typeof sectionUrl === 'string') refreshShownVaultSection(self.system, sectionUrl);
      } catch (error) {
        console.warn('[WebSocket] Could not refresh the shown vault section:', error);
      }
    }
  }

  if (isAiJobUpdateMessage(message)) {
    // Route to documentsService for AI job updates
    try {
      const documentsService = self.system?.get(DOCUMENTS_SERVICE);
      if (documentsService) {
        documentsService.send({
          type: 'ON_JOB_UPDATE',
          payload: message.data,
        });
      } else {
        console.debug('[WebSocket] documentsService not available (may not be in documents view)');
      }
    } catch (error) {
      console.warn('[WebSocket] Could not route AI job update to documentsService:', error);
    }

    // A parsed certificate has just saved a policy, so the insurance table is stale.
    if (isInsuranceParseCompleted(message.data)) {
      try {
        self.system?.get(MALPRACTICE_STATE)?.send({ type: 'ON_INSURANCE_PARSE_COMPLETED' });
      } catch (error) {
        console.warn('[WebSocket] Could not route insurance parse completion:', error);
      }
    }
  }

  // Always dispatch the generic message event for backward compatibility
  const genericEventName = `${context.config.eventNamespace}-message`;
  document.dispatchEvent(
    new CustomEvent(genericEventName, {
      detail: message,
    })
  );
};
